import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import {
    ActiveWorkflowDialog,
    ConflictsResponse,
    getTemplateTypeBadge,
    RequestFailure,
    TemplateDetailsDialog,
} from "./PublishedWorkflowTemplate";
import RenameConflictingWorkflowsDialog from "./RenameConflictingWorkflowsDialog";
import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import { NoAccess } from "components/icons/NoAccess";
import ScheduledIcon from "components/icons/ScheduledIcon";
import { TitleIcon } from "components/icons/TitleIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { WORKFLOWS_ROUTE } from "components/router/Routes";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import Tooltip from "components/tooltip/Tooltip";
import WasmVisualEditorView from "components/workflows/wasm-visual-editor/WasmVisualEditorView";
import WorkflowEditorV3View from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import publishTemplateStyle from "components/workflows/workflow-templates/published-workflow-template.scss";
import { deduceEditorVersion, isEditorV3Supported } from "components/workflows/WorkflowUtil";
import { AUTH_WORKFLOW_DEFAULT, AUTH_WORKFLOW_SECURED, AUTH_WORKFLOW_TEMPLATE_SAVE } from "domain/authority";
import { LaunchType, WorkflowEntityType } from "domain/workflows";
import { isCurrentTenantInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { ConflictBehavior } from "services/workflows/TemplateService";
import {
    ManifestWorkflowEditor,
    Profile,
    profileToNameMap,
    WorkflowEditorV3SupportedEntry,
} from "services/workflows/WorkflowService";
import { buildScheduleSummary, DayOfWeek } from "utils/scheduleUtils";
import { useTheme } from "utils/useTheme";

import assetGradingIcon from "assets/images/icons/assetGradingIcon.svg";
import lockIcon from "assets/images/icons/darkGreyLockIcon.svg";

import testIds from "testIds.json";

interface Props {
    showTemplateDetailsDialog: TemplateDetailsDialog;
    setShowTemplateDetailsDialog: React.Dispatch<React.SetStateAction<TemplateDetailsDialog>>;
    setAddedBadge: React.Dispatch<React.SetStateAction<boolean>>;
    setCount?: React.Dispatch<React.SetStateAction<number>>;
    requestFailure: RequestFailure | null;
    loading: boolean;
    conflictsException: ConflictsResponse | undefined;
    addedBadge: boolean;
    saveTemplate: (conflictBehavior: ConflictBehavior, renameTargets?: Record<string, string>) => void;
    showActiveWorkflowDialog: ActiveWorkflowDialog;
    setShowActiveWorkflowDialog: React.Dispatch<React.SetStateAction<ActiveWorkflowDialog>>;
    setSavedWorkflowActive: () => void;
    showSuccessNotificationToast: (active: boolean, isBmpTemplate: boolean) => void;
    activeWorkflowLoading: boolean;
    workflowEditors: ManifestWorkflowEditor[];
    workflowEditorV3Supported: WorkflowEditorV3SupportedEntry[];
    previewOnly: boolean;
    updatePriority: boolean;
}

const DOT = " • ";
// Workflow template feature introduced from BDE 7.14.0 and BMDE 5.5.0 which includes tabs in preview mode.
const BDE_DEDUCED_OLDER_EDITOR_VERSION = "7.14.0";
const BMDE_DEDUCED_OLDER_EDITOR_VERSION = "5.6.0";

const workflowTemplateDetailsDialog = (props: Props) => {
    const { t } = useTranslation();
    const [saveInProgress, setSaveInProgress] = React.useState(false);
    const [hasNewEditorLoaded, setHasNewEditorLoaded] = React.useState<boolean>(false);
    const [previewEditorCssClass, setPreviewEditorCssClass] = React.useState<string>(
        publishTemplateStyle.previewWorkflow
    );
    const [showRenameDialog, setShowRenameDialog] = React.useState(false);
    const theme = useTheme();

    const totalConnectedWorkflows = props.showTemplateDetailsDialog.details?.connectedWorkflows?.totalConnectedWorkflows
        ? props.showTemplateDetailsDialog.details.connectedWorkflows.totalConnectedWorkflows
        : 0;
    const subWorkflowText =
        totalConnectedWorkflows > 1 ? t("workflowTemplate.subWorkflows") : t("workflowTemplate.subWorkflow");
    const isBmpTemplate = props.showTemplateDetailsDialog.details?.profile === Profile.BMP;

    React.useEffect(() => {
        if (hasNewEditorLoaded && props.showTemplateDetailsDialog.details) {
            if (
                ![BDE_DEDUCED_OLDER_EDITOR_VERSION, BMDE_DEDUCED_OLDER_EDITOR_VERSION].includes(
                    deduceEditorVersion(props.showTemplateDetailsDialog.details.editorVersion)
                )
            ) {
                setPreviewEditorCssClass(publishTemplateStyle.previewWorkflowWithBorder);
            }
        }
    }, [hasNewEditorLoaded]);
    const isSecured =
        props.showTemplateDetailsDialog.details?.profile === Profile.BMP &&
        !!props.showTemplateDetailsDialog.details?.secured &&
        !(userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED]) && isCurrentTenantInternal());

    React.useEffect(() => {
        if (!props.loading) {
            setSaveInProgress(false);
        }
    }, [props.loading]);

    const handleFailureSecondaryClick = () => {
        if (props.conflictsException) {
            setSaveInProgress(true);
            props.saveTemplate(ConflictBehavior.OVERWRITE);
            return;
        }
        props.setShowTemplateDetailsDialog({ show: false, details: undefined });
        props.setAddedBadge(false);
        props.setCount?.((prev) => ++prev);
    };

    const handleRenameClick = () => {
        setShowRenameDialog(true);
    };

    const handleRenameSubmit = (renameTargets: Record<string, string>) => {
        setShowRenameDialog(false);
        setSaveInProgress(true);
        props.saveTemplate(ConflictBehavior.RENAME, renameTargets);
    };

    const isV3 =
        !!props.showTemplateDetailsDialog.details &&
        isEditorV3Supported(
            props.workflowEditorV3Supported,
            props.showTemplateDetailsDialog.details.profile,
            props.showTemplateDetailsDialog.details.editorVersion
        );

    return (
        <>
            <Modal
                isOpen={props.showTemplateDetailsDialog.show}
                hideModal={() => {
                    props.setShowTemplateDetailsDialog({ show: false, details: undefined });
                    props.setAddedBadge(false);
                    props.setCount?.((prev) => ++prev);
                }}
                titleIcon={props.requestFailure ? <TitleIcon iconType="WARNING" /> : ""}
                modalTitle={props.requestFailure?.title || ""}
            >
                <div
                    className={
                        saveInProgress
                            ? publishTemplateStyle.saveInProgress
                            : !props.requestFailure?.message
                              ? publishTemplateStyle.publishedTemplateDetailsDialog
                              : publishTemplateStyle.publishedTemplateDetailsErrorDialog
                    }
                >
                    {props.loading ? (
                        <div className={publishTemplateStyle.saveTemplate}>
                            <LoadingIndicator />
                            <div className={publishTemplateStyle.message}>
                                {t("workflowTemplate.workflowTemplateDetailsDialog.addingTemplateToWorkflows")}
                            </div>
                        </div>
                    ) : props.requestFailure ? (
                        <>
                            <div>{props.requestFailure.message} </div>
                            {props.requestFailure.scheduleWarning && (
                                <div className={publishTemplateStyle.scheduleMessage}>
                                    {props.requestFailure.scheduleWarning}
                                </div>
                            )}
                            {props.conflictsException && (
                                <div className={publishTemplateStyle.conflictingSubWorkflows}>
                                    <ul>
                                        {props.conflictsException.conflictingTemplate && (
                                            <li key={props.conflictsException.conflictingTemplate}>
                                                {props.conflictsException.conflictingTemplate}
                                            </li>
                                        )}

                                        {props.conflictsException.conflictingSubWorkflows.map((each) => (
                                            <li key={each}>{each}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <ButtonContainer>
                                <Button
                                    variant={"PRIMARY"}
                                    onClick={
                                        props.conflictsException
                                            ? handleRenameClick
                                            : () => window.location.replace(WORKFLOWS_ROUTE.path)
                                    }
                                    testId={testIds.common.dialog.primaryButton}
                                >
                                    {props.conflictsException
                                        ? t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.rename")
                                        : t(
                                              "workflowTemplate.workflowTemplateDetailsDialog.error.generic.goToWorkflows"
                                          )}
                                </Button>

                                <Button
                                    variant={"SECONDARY"}
                                    onClick={handleFailureSecondaryClick}
                                    testId={testIds.common.dialog.closeButton}
                                >
                                    {props.conflictsException
                                        ? t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.overwrite")
                                        : t("Common.cancel")}
                                </Button>
                            </ButtonContainer>
                        </>
                    ) : (
                        props.showTemplateDetailsDialog.details && (
                            <div className={publishTemplateStyle.template}>
                                <div className={publishTemplateStyle.templateContent}>
                                    <div className={publishTemplateStyle.badges}>
                                        <div>
                                            {getTemplateTypeBadge(
                                                props.showTemplateDetailsDialog.details.templateType,
                                                t,
                                                theme.readyToUseSkyBlueColor,
                                                theme.modularPurpleColor,
                                                theme.integrationGreenColor
                                            )}
                                        </div>
                                        {props.showTemplateDetailsDialog.details.update && props.updatePriority ? (
                                            <div className={publishTemplateStyle.added}>
                                                <StatusBadge
                                                    values={[
                                                        {
                                                            status: Status.WARNING,
                                                            title: t(
                                                                "workflowTemplate.workflowTemplateDetailsDialog.update"
                                                            ),
                                                        },
                                                    ]}
                                                    tooltip={true}
                                                />
                                            </div>
                                        ) : (
                                            (props.addedBadge || props.showTemplateDetailsDialog.details.saved) && (
                                                <div className={publishTemplateStyle.added}>
                                                    <StatusBadge
                                                        values={[
                                                            {
                                                                status: Status.SUCCESS,
                                                                title: t(
                                                                    "workflowTemplate.workflowTemplateDetailsDialog.added"
                                                                ),
                                                            },
                                                        ]}
                                                        tooltip={true}
                                                    />
                                                </div>
                                            )
                                        )}
                                        {props.showTemplateDetailsDialog.details.secured && (
                                            <div className={publishTemplateStyle.added}>
                                                <StatusBadge
                                                    values={[
                                                        {
                                                            status: Status.NEUTRAL,
                                                            title: t(
                                                                "workflowTemplate.workflowTemplateDetailsDialog.locked"
                                                            ),
                                                        },
                                                    ]}
                                                    tooltip={true}
                                                />
                                            </div>
                                        )}
                                        {props.showTemplateDetailsDialog.details.assetGrading && (
                                            <Tooltip content={t("workflowTemplate.assetGrading")}>
                                                <div
                                                    className={classNames(
                                                        publishTemplateStyle.added,
                                                        publishTemplateStyle.iconSpacing
                                                    )}
                                                >
                                                    <img
                                                        src={assetGradingIcon}
                                                        alt="asset grading"
                                                        style={{ width: 14, height: 14 }}
                                                    />
                                                </div>
                                            </Tooltip>
                                        )}
                                    </div>

                                    <div className={publishTemplateStyle.dataContainer}>
                                        <div className={publishTemplateStyle.title}>
                                            {props.showTemplateDetailsDialog.details.name}
                                        </div>
                                        <div className={publishTemplateStyle.textstyle}>
                                            {profileToNameMap.get(
                                                props.showTemplateDetailsDialog.details.profile as Profile
                                            )}{" "}
                                            {props.showTemplateDetailsDialog.details.editorVersion}{" "}
                                            {totalConnectedWorkflows !== 0 &&
                                                DOT + totalConnectedWorkflows + " " + subWorkflowText}
                                        </div>
                                        {props.showTemplateDetailsDialog.details.schedule && (
                                            <div className={publishTemplateStyle.scheduleRow}>
                                                <ScheduledIcon color={theme.textGreyColor} size={14} />
                                                <span>
                                                    {buildScheduleSummary(
                                                        {
                                                            launchSchedule:
                                                                props.showTemplateDetailsDialog.details.schedule
                                                                    .frequency,
                                                            everyHours:
                                                                props.showTemplateDetailsDialog.details.schedule
                                                                    .recurrence.every || 1,
                                                            launchTimeHours:
                                                                props.showTemplateDetailsDialog.details.schedule.recurrence.time?.split(
                                                                    ":"
                                                                )[0] || "00",
                                                            launchTimeMinutes:
                                                                props.showTemplateDetailsDialog.details.schedule.recurrence.time?.split(
                                                                    ":"
                                                                )[1] || "00",
                                                            timezone:
                                                                props.showTemplateDetailsDialog.details.schedule
                                                                    .timezone,
                                                            daysOfWeek: (props.showTemplateDetailsDialog.details
                                                                .schedule.recurrence.days || []) as DayOfWeek[],
                                                            dayOfMonth:
                                                                props.showTemplateDetailsDialog.details.schedule
                                                                    .recurrence.day || 1,
                                                        },
                                                        t
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {props.showTemplateDetailsDialog.details.useCases.length > 0 ||
                                        props.showTemplateDetailsDialog.details.coreActivities.length > 0 ||
                                        props.showTemplateDetailsDialog.details.processors.length > 0 ? (
                                            <div className={publishTemplateStyle.categories}>
                                                <ul>
                                                    {props.showTemplateDetailsDialog.details.useCases.map(
                                                        (data: string, index: number) => (
                                                            <li className={publishTemplateStyle.item} key={index}>
                                                                {data}
                                                            </li>
                                                        )
                                                    )}
                                                    {props.showTemplateDetailsDialog.details.coreActivities.map(
                                                        (data: string, index: number) => (
                                                            <li className={publishTemplateStyle.item} key={index}>
                                                                {data}
                                                            </li>
                                                        )
                                                    )}
                                                    {props.showTemplateDetailsDialog.details.processors.map(
                                                        (data: string, index: number) => (
                                                            <li className={publishTemplateStyle.item} key={index}>
                                                                {data}
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            </div>
                                        ) : (
                                            <></>
                                        )}

                                        <div className={publishTemplateStyle.details}>
                                            {props.showTemplateDetailsDialog.details.description && (
                                                <div className={publishTemplateStyle.description}>
                                                    <div className={publishTemplateStyle.title}>
                                                        {t(
                                                            "workflowTemplate.workflowTemplateDetailsDialog.description"
                                                        )}
                                                    </div>
                                                    <div className={publishTemplateStyle.content}>
                                                        {props.showTemplateDetailsDialog.details.description}
                                                    </div>
                                                </div>
                                            )}
                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.subWorkflows
                                                .length ||
                                            props.showTemplateDetailsDialog.details.connectedWorkflows
                                                .runProcessFlowWorkflows.length ||
                                            props.showTemplateDetailsDialog.details.connectedWorkflows
                                                .gradingPanelWorkflows.length ||
                                            props.showTemplateDetailsDialog.details.connectedWorkflows.collectWorkflows
                                                .length ? (
                                                <div className={publishTemplateStyle.description}>
                                                    <div className={publishTemplateStyle.title}>
                                                        {t("workflowTemplate.subWorkflowsTitle")}
                                                    </div>
                                                    <div className={publishTemplateStyle.content}>
                                                        <ul>
                                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.subWorkflows.map(
                                                                (data: string, index: number) => (
                                                                    <li
                                                                        className={publishTemplateStyle.item}
                                                                        key={index}
                                                                    >
                                                                        {data}
                                                                    </li>
                                                                )
                                                            )}
                                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.runProcessFlowWorkflows.map(
                                                                (data: string, index: number) => (
                                                                    <li
                                                                        className={publishTemplateStyle.item}
                                                                        key={index}
                                                                    >
                                                                        {data}
                                                                    </li>
                                                                )
                                                            )}
                                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.runDiagnoseWorkflows.map(
                                                                (data: string, index: number) => (
                                                                    <li
                                                                        className={publishTemplateStyle.item}
                                                                        key={index}
                                                                    >
                                                                        {data}
                                                                    </li>
                                                                )
                                                            )}
                                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.gradingPanelWorkflows.map(
                                                                (data: string, index: number) => (
                                                                    <li
                                                                        className={publishTemplateStyle.item}
                                                                        key={index}
                                                                    >
                                                                        {data}
                                                                    </li>
                                                                )
                                                            )}
                                                            {props.showTemplateDetailsDialog.details.connectedWorkflows.collectWorkflows.map(
                                                                (data: string, index: number) => (
                                                                    <li
                                                                        className={publishTemplateStyle.item}
                                                                        key={index}
                                                                    >
                                                                        {data}
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </div>
                                    {!props.addedBadge && !props.previewOnly ? (
                                        userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_SAVE]) ? (
                                            <div className={publishTemplateStyle.actionButtons}>
                                                <ButtonContainer>
                                                    <Button
                                                        variant={"PRIMARY"}
                                                        onClick={() => {
                                                            setSaveInProgress(true);
                                                            props.saveTemplate(ConflictBehavior.DEFAULT);
                                                        }}
                                                        testId={testIds.common.dialog.primaryButton}
                                                    >
                                                        {t(
                                                            "workflowTemplate.workflowTemplateDetailsDialog.addTemplate"
                                                        )}
                                                    </Button>
                                                </ButtonContainer>
                                            </div>
                                        ) : (
                                            <div className={publishTemplateStyle.noPermissionContainer}>
                                                <NoAccess color={theme.textColor} />
                                                <div className={publishTemplateStyle.noPermissionText}>
                                                    {t("workflowTemplate.workflowTemplateDetailsDialog.noAccess")}{" "}
                                                </div>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                                <div
                                    className={
                                        isV3 ? publishTemplateStyle.previewWorkflowWithBorder : previewEditorCssClass
                                    }
                                >
                                    {isSecured ? (
                                        <div className={publishTemplateStyle.securedContainer}>
                                            <img
                                                src={lockIcon}
                                                alt="secured"
                                                style={{ marginRight: 4, width: 27, height: 32 }}
                                            />

                                            <div className={publishTemplateStyle.securedText}>
                                                {" "}
                                                {t("workflowTemplate.workflowTemplateDetailsDialog.lockedText")}
                                            </div>
                                        </div>
                                    ) : isV3 ? (
                                        <WorkflowEditorV3View
                                            key={props.showTemplateDetailsDialog.details.uuid}
                                            initialTabs={[
                                                { workflowUuid: props.showTemplateDetailsDialog.details.uuid },
                                            ]}
                                            entityType={WorkflowEntityType.TEMPLATE}
                                            previewMode={true}
                                        />
                                    ) : (
                                        <WasmVisualEditorView
                                            key={props.showTemplateDetailsDialog.details.uuid}
                                            profile={props.showTemplateDetailsDialog.details.profile}
                                            version={props.showTemplateDetailsDialog.details.editorVersion}
                                            workflowEditors={props.workflowEditors}
                                            uuid={props.showTemplateDetailsDialog.details.uuid}
                                            data-testid={
                                                testIds.workArea.workflows.manageWorkflowDialog.visualEditor.itself
                                            }
                                            actionType={"create"}
                                            entityType={WorkflowEntityType.TEMPLATE}
                                            preview={true}
                                            preventNameChange={true}
                                            setHasNewEditorLoaded={setHasNewEditorLoaded}
                                        />
                                    )}
                                </div>
                            </div>
                        )
                    )}
                </div>
            </Modal>
            <Modal
                modalTitle={
                    isBmpTemplate
                        ? t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.title")
                        : t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.title")
                }
                isOpen={
                    props.showActiveWorkflowDialog.show &&
                    userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_DEFAULT]) &&
                    props.showTemplateDetailsDialog.details?.launchType !== LaunchType.MANUAL
                }
                hideModal={() => {
                    props.showSuccessNotificationToast(false, isBmpTemplate);
                    props.setShowActiveWorkflowDialog({
                        show: false,
                        workflowUuid: "",
                    });
                }}
            >
                {props.activeWorkflowLoading ? (
                    <LoadingIndicator />
                ) : (
                    <div className={publishTemplateStyle.setActiveDialog}>
                        <div>
                            {isBmpTemplate
                                ? t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.message")
                                : t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.message")}
                        </div>

                        <ButtonContainer>
                            <Button
                                variant={"PRIMARY"}
                                onClick={() => {
                                    props.showSuccessNotificationToast(false, isBmpTemplate);
                                    props.setShowActiveWorkflowDialog({
                                        show: false,
                                        workflowUuid: "",
                                    });
                                }}
                                testId={testIds.common.dialog.closeButton}
                            >
                                {isBmpTemplate
                                    ? t(
                                          "workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.dontSetActiveButton"
                                      )
                                    : t(
                                          "workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.dontSetActiveButton"
                                      )}
                            </Button>

                            <Button
                                variant={"SECONDARY"}
                                onClick={() => {
                                    props.setSavedWorkflowActive();
                                }}
                                testId={testIds.common.confirmationDialog.confirmButton}
                            >
                                {isBmpTemplate
                                    ? t(
                                          "workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.setAsActiveButton"
                                      )
                                    : t(
                                          "workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.setAsActiveButton"
                                      )}
                            </Button>
                        </ButtonContainer>
                    </div>
                )}
            </Modal>
            <RenameConflictingWorkflowsDialog
                show={showRenameDialog}
                conflictsException={props.conflictsException!}
                onClose={() => setShowRenameDialog(false)}
                onRename={handleRenameSubmit}
            />
        </>
    );
};

export default workflowTemplateDetailsDialog;
